import {
  AmendmentVisibility,
  CollaboratorRole,
  CollaboratorType,
  JobBillingType,
  JobDTO,
  JobPaymentInfoStatus,
  JobProtoDTO,
  JobResolution,
  JobStatus,
  JobType,
  PayoutSplitDTO,
  TaskDesignee,
} from './job'
import {
  AmendmentVisibility as AmendmentVisibilityProto,
  Job,
  Resolution,
  Status,
  WaitingOn,
} from '@taxfyle/api-internal/internal/job_pb'
import { BadRequest } from 'fejl'
import { decimalToNumber, timestampToISO } from '../../utils/grpc-util'
import { match } from 'utils/pattern-match-util'
import { isNotNil } from 'utils/object-util'

export function mapGrpcJobStatus(status: Status): JobStatus {
  switch (status) {
    case Status.CLAIMED:
      return 'CLAIMED'
    case Status.CLOSED:
      return 'CLOSED'
    case Status.IDLE:
      return 'IDLE'
    case Status.ON_HOLD:
      return 'ON_HOLD'
    case Status.UNCLAIMED:
      return 'UNCLAIMED'
    case Status.UNDER_CONSTRUCTION:
      return 'UNDER_CONSTRUCTION'
    case Status.INFO_GATHERING:
      return 'INFO_GATHERING'
    default:
      throw new BadRequest('Job is of an undefined status')
  }
}

function mapGrpcJobResolution(resolution: Resolution): JobResolution {
  switch (resolution) {
    case Resolution.RESOLUTION_UNSPECIFIED:
      return null
    case Resolution.DONE:
      return 'DONE'
    case Resolution.CANCELLED:
      return 'CANCELLED'
    case Resolution.TEST:
      return 'TEST'
    default:
      throw new BadRequest('Job resolution unknown')
  }
}

function mapGrpcJobPaymentInfoStatus(
  info: Job.PaymentInfoStatus,
): JobPaymentInfoStatus {
  switch (info) {
    case Job.PaymentInfoStatus.NOT_PROVIDED:
      return 'NOT_PROVIDED'
    case Job.PaymentInfoStatus.PROVIDED:
      return 'PROVIDED'
    case Job.PaymentInfoStatus.REQUESTED:
      return 'REQUESTED'
    default:
      throw new BadRequest('Job payment info status unknown')
  }
}

function mapGrpcJobWaitingOn(waiting: WaitingOn) {
  switch (waiting) {
    case WaitingOn.WAITING_ON_CLIENT:
      return 'Client'
    case WaitingOn.WAITING_ON_PRO:
      return 'Provider'
    case WaitingOn.WAITING_ON_UNSPECIFIED:
      return null
    default:
      throw new BadRequest('Job waiting unknown')
  }
}

function mapGrpcJobTaskDesignee(
  designee: Job.Milestone.MilestoneTask.TaskDesignee,
): TaskDesignee {
  switch (designee) {
    case Job.Milestone.MilestoneTask.TaskDesignee.CLIENT:
      return 'Client'
    case Job.Milestone.MilestoneTask.TaskDesignee.PROVIDER:
      return 'Provider'
    default:
      throw new BadRequest('Job task designee unknown')
  }
}

function mapGrpcJobAmendmentVisibility(
  visibility: AmendmentVisibilityProto,
): AmendmentVisibility {
  switch (visibility) {
    case AmendmentVisibilityProto.INTERNAL:
      return 'INTERNAL'
    case AmendmentVisibilityProto.PUBLIC:
      return 'PUBLIC'
    default:
      throw new BadRequest('Job amendment visibility unknown')
  }
}

function mapGrpcJobMemberType(type: Job.MemberType): CollaboratorType {
  switch (type) {
    case Job.MemberType.CLIENT:
      return 'CLIENT'
    case Job.MemberType.PROVIDER:
      return 'PROVIDER'
    default:
      throw new BadRequest('Job member type unknown')
  }
}

function mapGrpcJobMemberRole(role: Job.MemberRole): CollaboratorRole {
  switch (role) {
    case Job.MemberRole.CHAMPION:
      return 'CHAMPION'
    case Job.MemberRole.SUPPORTING:
      return 'SUPPORTING'
    default:
      throw new BadRequest('Job member role unknown')
  }
}

function mapGrpcJobType(jobType?: Job.JobType.AsObject): JobType {
  if (!jobType) {
    return { type: 'UNSPECIFIED' }
  }

  const { consultation, bookkeeping, tax, misc } = jobType

  if (consultation) {
    return { type: 'CONSULTATION' }
  }

  if (bookkeeping) {
    return {
      type: 'BOOKKEEPING',
    }
  }

  if (misc) {
    return {
      type: 'MISC',
    }
  }

  if (tax) {
    const prepJobLink = tax.jobLinks?.prepJobLink
    const reviewJobLink = tax.jobLinks?.reviewJobLink

    if (prepJobLink) {
      return {
        type: 'TAX',
        jobLinks: { prepAndReviewLink: { type: 'PREP', id: prepJobLink.id } },
      }
    }

    if (reviewJobLink) {
      return {
        type: 'TAX',
        jobLinks: {
          prepAndReviewLink: { type: 'REVIEW', id: reviewJobLink.id },
        },
      }
    }

    return {
      type: 'TAX',
      jobLinks: null,
    }
  }

  return { type: 'UNSPECIFIED' }
}

function mapGrpcPayoutSplit(
  payoutSplit: Job.PayoutSplit.AsObject,
): PayoutSplitDTO {
  return {
    auto_adjusted: payoutSplit.autoAdjusted,
    date_auto_adjusted: timestampToISO(payoutSplit.dateAutoAdjusted) ?? null,
    date_allocated_manually:
      timestampToISO(payoutSplit.dateAllocatedManually) ?? null,
    allocated_manually_by_public_id:
      payoutSplit.allocatedManuallyByPublicId?.value ?? null,
    allocations: payoutSplit.allocationsList.map((a) => ({
      user_public_id: a.userPublicId,
      amount: decimalToNumber(a.amount!),
      authorized_partial_payout_amount: decimalToNumber(
        a.authorizedPartialPayoutAmount!,
      ),
    })),
  }
}

function mapCustomerRatingMethod(
  method: Job.Legend.RatingConfig.CustomerRatingMethod,
) {
  switch (method) {
    case Job.Legend.RatingConfig.CustomerRatingMethod.STARS:
      return 'stars'
    case Job.Legend.RatingConfig.CustomerRatingMethod.THUMBS:
      return 'thumbs'
    default:
      return 'thumbs'
  }
}

function mapGrpcLegend(legend: Job.Legend.AsObject) {
  return {
    id: legend.id,
    name: legend.name,
    version: legend.version,
    allowScheduling: legend.allowScheduling,
    alwaysEnableVoiceCall: legend.alwaysEnableVoiceCall,
    exclude_legacy_years: legend.excludeLegacyYears,
    exclude_legacy_states: legend.excludeLegacyStates,
    price_unit: legend.priceUnit?.value,
    skip_billing: legend.skipBilling,
    skip_quote: legend.skipQuote,
    allow_pro_selection: legend.allowProSelection,
    billing_config: legend.billingConfig
      ? {
          allow_manual_payment_collection:
            legend.billingConfig.allowManualPaymentCollection,
          defer_payment_info_collection:
            legend.billingConfig.deferPaymentInfoCollection,
        }
      : undefined,
    deadline_config: legend.deadlineConfig
      ? {
          enabled: legend.deadlineConfig.enabled,
          expr: legend.deadlineConfig.expr,
        }
      : undefined,
    rating_config: legend.ratingConfig
      ? {
          platformRatingEnabled: legend.ratingConfig.platformRatingEnabled,
          proRatingEnabled: legend.ratingConfig.proRatingEnabled,
          customerRatingMethod: mapCustomerRatingMethod(
            legend.ratingConfig.customerRatingMethod,
          ),
        }
      : undefined,
  }
}

function mapGrpcBillingType(billingType: Job.BillingType): JobBillingType {
  return match(billingType, {
    [Job.BillingType.EXTERNAL]: () => 'EXTERNAL',
    [Job.BillingType.STRIPE]: () => 'STRIPE',
  })
}

export function mapJobProtoToJobDTO(payload: JobProtoDTO): Partial<JobDTO> {
  return {
    workspace_id: payload.workspaceId,
    owner_team_id: payload.ownerTeamId?.value,
    team_ids: payload.teamIdsList,
    legend: payload.legend ? mapGrpcLegend(payload.legend) : undefined,
    members: payload.membersList.map((m) => ({
      team_id: m.teamId,
      role: mapGrpcJobMemberRole(m.role),
      type: mapGrpcJobMemberType(m.type),
      user: {
        email: m.email,
        family_name: m.familyName,
        display_name: m.displayName,
        given_name: m.givenName,
        picture: m.picture,
        user_id: m.userId,
        user_public_id: m.id,
      },
    })),
    date_modified: timestampToISO(payload.dateModified),
    date_last_activity: timestampToISO(payload.dateLastActivity),
    stripe_charges: payload.stripeChargesList,
    primary_amount: payload.primaryAmount
      ? decimalToNumber(payload.primaryAmount)
      : undefined,
    paid_amount: payload.paidAmount
      ? decimalToNumber(payload.paidAmount)
      : undefined,
    total: payload.total ? decimalToNumber(payload.total) : undefined,
    requested_pro_id: payload.requestedProId?.value ?? null,
    requested_pro_public_id: payload.requestedProPublicId?.value ?? null,
    legend_name: payload.legend?.name,
    date_deadline: timestampToISO(payload.dateDeadline),
    date_created: timestampToISO(payload.dateCreated),
    resolution: mapGrpcJobResolution(payload.resolution),
    date_transmitted: timestampToISO(payload.dateTransmitted),
    date_accepted: timestampToISO(payload.dateAccepted),
    date_closed: timestampToISO(payload.dateClosed),
    answers: payload.answersList.map((a) => ({
      id: a.id,
      answer: JSON.parse(a.value),
    })),
    payment_info_status: mapGrpcJobPaymentInfoStatus(payload.paymentInfoStatus),
    id: payload.id,
    short_id: payload.shortId,
    job_specs: payload.jobSpecsList,
    name: payload.name?.value || payload.description?.value,
    description: payload.description?.value,
    provider_cut_amount: payload.providerCutAmount
      ? decimalToNumber(payload.providerCutAmount)
      : undefined,
    coupon_codes: payload.couponCodesList,
    status: mapGrpcJobStatus(payload.status),
    info_lines: payload.infoLinesList,
    layerConversation: payload.conversationId?.value,
    milestones: payload.milestonesList.map((m) => ({
      id: m.id,
      title: m.title,
      description: m.description,
      completed: m.completed,
      tasks: m.tasksList.map((t) => ({
        id: t.id,
        title: t.title,
        description: t.description?.value,
        designee: mapGrpcJobTaskDesignee(t.designee),
        completed: t.completed,
        date_completed: timestampToISO(t.dateCompleted),
        completed_by_id: t.completedById?.value,
        completed_by_public_id: t.completedByPublicId?.value,
      })),
    })),
    waiting_on: mapGrpcJobWaitingOn(payload.waitingOn),
    amendments: payload.amendmentsList.map((a) => ({
      description: a.description,
      internal_note: a.internalNote,
      provider_amount: decimalToNumber(a.providerAmount),
      customer_amount: decimalToNumber(a.customerAmount),
      visibility: mapGrpcJobAmendmentVisibility(a.visibility),
      date_created: timestampToISO(a.dateCreated)!,
      creator: {
        email: a.creator.email,
        family_name: a.creator.familyName,
        display_name: a.creator.displayName,
        given_name: a.creator.givenName,
        picture: a.creator.picture,
        user_id: a.creator.userId,
        user_public_id: a.creator.id,
      },
    })),
    payout_split: payload.payoutSplit
      ? mapGrpcPayoutSplit(payload.payoutSplit)
      : undefined,
    calling_actor_allocation: payload.callingActorAllocation
      ? decimalToNumber(payload.callingActorAllocation)
      : undefined,
    job_scope_items: payload.jobScopeItemsList.map((i) => ({
      id: i.id,
      name: i.name,
      description: i.description,
      quantity: i.quantity,
      courtesy_quantity: i.courtesyQuantity?.value,
      inventory_item_id: i.inventoryItemId,
      inventory_item_version_id: i.inventoryItemVersionId,
      price: i.price ? decimalToNumber(i.price) : undefined,
      pro_price: i.proPrice ? decimalToNumber(i.proPrice) : undefined,
      bundle_id: isNotNil(i.bundleId) ? i.bundleId.value : null,
      custom_item_id: isNotNil(i.customItemId) ? i.customItemId.value : null,
    })),
    billing_type: mapGrpcBillingType(payload.billingType),
    consultation: payload.consultation,
    type: mapGrpcJobType(payload.type),
  }
}
